/**
 * ConsentRequestCard — the egress-consent prompt (FS-676,
 * docs/egress-consent-design.md §Follow-up: interactive consent).
 *
 * Rendered under an assistant message whose turn WOULD have used a public
 * egress surface (web search) but the tenant's `ask` policy had no stored
 * verdict for this conversation. Approve records a `granted` verdict and
 * re-runs the question web-armed; Deny records `denied` and the KG-only
 * answer above stands — neither happens silently, which is the whole point
 * of the admin's `ask` posture.
 */

import { Check, Globe, X } from 'lucide-react'
import { useTranslation } from '@/i18n'
import type { ConsentRequest } from '../../types'

interface ConsentRequestCardProps {
  consentRequest: ConsentRequest
  onRespond?: (verdict: 'granted' | 'denied') => void
}

export function ConsentRequestCard({ consentRequest, onRespond }: ConsentRequestCardProps) {
  const { t } = useTranslation()
  const { status } = consentRequest

  return (
    <div
      data-testid="consent-request-card"
      className="mt-4 rounded-lg border border-amber-200 dark:border-amber-900/60 bg-amber-50/70 dark:bg-amber-900/15 px-4 py-3"
    >
      <div className="flex items-start gap-3">
        <Globe className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {t('chatv2:consent.title', {
              defaultValue: 'Search the public web for this question?',
            })}
          </p>
          <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
            {t('chatv2:consent.body', {
              defaultValue:
                'Your plant data could not answer this. With your permission, this conversation can use a public web search — the question text will leave your deployment. Your administrator requires this to be asked once per conversation.',
            })}
          </p>
          {status === 'pending' && (
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => onRespond?.('granted')}
                className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-amber-700"
              >
                <Check className="h-3.5 w-3.5" />
                {t('chatv2:consent.allow', { defaultValue: 'Allow web search' })}
              </button>
              <button
                onClick={() => onRespond?.('denied')}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-card px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 shadow-sm transition-colors hover:bg-gray-50 dark:hover:bg-dark-muted"
              >
                <X className="h-3.5 w-3.5" />
                {t('chatv2:consent.deny', { defaultValue: 'Not for this conversation' })}
              </button>
            </div>
          )}
          {status === 'granted' && (
            <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-green-700 dark:text-green-400">
              <Check className="h-3.5 w-3.5" />
              {t('chatv2:consent.granted', {
                defaultValue: 'Web search allowed for this conversation.',
              })}
            </p>
          )}
          {status === 'denied' && (
            <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
              <X className="h-3.5 w-3.5" />
              {t('chatv2:consent.denied', {
                defaultValue: 'Web search stays off for this conversation.',
              })}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
